import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontMerriweather } from "@product/scmp-sdk";

import {
  AdSlotContainer,
  StyledSlot,
} from "scmp-app/components/advertisement/ad-slots/ad-slot/styles";
import { ContentItemSeriesBanner } from "scmp-app/components/content/content-item-render/variants/series-banner";
import type { WithStickyStackedHeightProps } from "scmp-app/lib/styles";
import { setFullViewportWidthStyles } from "scmp-app/lib/styles";

import { SeriesArticleOtherScmpSeries } from "./other-scmp-series";

export const Container = styled.div`
  ${setFullViewportWidthStyles()}
  padding: 20px;
  ${props => props.theme.breakpoints.up("tablet")} {
    padding: 40px;
  }
`;

export const Wrapper = styled.div`
  display: grid;
  grid:
    "." 20px
    "banner" min-content
    "." 20px
    "top-banner" min-content
    "." 28px
    "list"
    / minmax(0, 600px);
  justify-content: center;

  ${props => props.theme.breakpoints.up("tablet")} {
    grid:
      "." 40px
      "banner" min-content
      "." 32px
      "top-banner" min-content
      "." 32px
      "list"
      "." 32px
      "other-series" min-content
      / minmax(0, 800px);
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    grid:
      "top-banner top-banner top-banner top-banner" min-content
      ". . . ." 60px
      "banner banner banner banner" min-content
      ". . . ." 48px
      ". list .  right" min-content
      ". . . ." 40px
      "other-series other-series other-series other-series" min-content
      / 60px 1fr 56px 300px;

    max-inline-size: 1024px;
    margin-inline: auto;
  }
`;

type ItemContainerProps = {
  $isAds?: boolean;
};

export const ItemContainer = styled.div<ItemContainerProps>`
  ${props =>
    props.$isAds &&
    css`
      display: flex;
      align-items: center;
      justify-content: center;
    `}

  ${props => props.theme.breakpoints.up("desktop")} {
    display: ${props => (props.$isAds ? "none" : "block")};
  }
`;

type ListProps = {
  $isReverseOrder?: boolean;
};
export const List = styled.div<ListProps>`
  grid-area: list;

  display: flex;

  inline-size: 100%;
  max-inline-size: 400px;
  margin-inline: auto;
  padding-inline: 0;

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 608px;
  }
  ${props => props.theme.breakpoints.up("desktop")} {
    max-inline-size: none;
    padding-inline: 0;
  }

  ${props =>
    props.$isReverseOrder
      ? css`
          flex-direction: column-reverse;
          ${ItemContainer} {
            &:not(:first-child) {
              margin-block-end: 28px;
              padding-block-end: 28px;
              border-block-end: 1px solid rgb(0, 0, 0, 0.2);
            }
          }
        `
      : css`
          flex-direction: column;
          ${ItemContainer} {
            &:not(:last-child) {
              margin-block-end: 28px;
              padding-block-end: 28px;
              border-block-end: 1px solid rgb(0, 0, 0, 0.2);
            }
          }
        `}
`;

export const SeriesItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${props => props.theme.breakpoints.up("tablet")} {
    flex-direction: row;
  }
`;

export const SeriesNumber = styled.div`
  color: #2c4692;
  font-weight: 400;
  font-size: 24px;
  font-family: ${fontMerriweather};
  font-style: italic;
  font-variant-numeric: lining-nums proportional-nums;
  span {
    padding-inline: 2px;

    text-align: center;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-inline-end: 19px;
  }
`;

export const MobileAndTabletAdSlot = styled.div`
  display: block;

  ${props => props.theme.breakpoints.up("desktop")} {
    display: none;
  }
`;

export const DesktopAdSlot = styled.div`
  display: none;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: block;
  }
`;

export const BannerAdSlot = styled.div`
  grid-area: top-banner;

  ${props => props.theme.breakpoints.up("desktop")} {
    ${setFullViewportWidthStyles()}
  }
`;

export const StyledSeriesArticleOtherScmpSeries = styled(SeriesArticleOtherScmpSeries)`
  grid-area: other-series;

  display: none;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: grid;
  }
`;

export const StyledContentItemSeriesBanner = styled(ContentItemSeriesBanner)`
  grid-area: banner;
`;

export const RightSectionSticky = styled.div<WithStickyStackedHeightProps>`
  display: none;

  ${props => props.theme.breakpoints.up("desktop")} {
    display: block;
    flex: auto;
  }

  && ${AdSlotContainer} {
    display: flex;
    align-items: flex-start;

    block-size: 100%;

    ${StyledSlot} {
      position: sticky;
      inset-block-start: ${props => props.$stickyStackedHeight + 50}px;

      block-size: auto;
    }
  }
`;

export const RightSection = styled.div`
  flex: auto;
`;

export const Right = styled.div`
  grid-area: right;

  display: none;
  flex-direction: column;
  gap: 40px;
  ${props => props.theme.breakpoints.up("desktop")} {
    display: flex;
  }
`;

export const ActionWidget = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: center;
`;
